import { ProjectMeta, getProjects } from '@/lib/content';
import PageMeta from '@/shared/components/PageMeta';
import AcademicLayout from '@/shared/components/AcademicLayout';
import Markdown from '@/shared/components/Markdown';
import { useLanguage } from '@/shared/contexts/LanguageContext';
import { getText } from '@/shared/utils/i18n';
import ExternalLink from '@/shared/ui/external-link';
import { motion } from 'framer-motion';
import { ArrowUpRight, CircleDot, CheckCircle2, Archive, Users } from 'lucide-react';
import { useMemo } from 'react';

const statusConfig: Record<
  ProjectMeta['status'],
  { icon: typeof CircleDot; className: string; labelKey: string }
> = {
  active: {
    icon: CircleDot,
    className: 'bg-orange-500/10 border-orange-500/20 text-orange-600 dark:text-orange-400',
    labelKey: 'statusActive'
  },
  shipped: {
    icon: CheckCircle2,
    className: 'bg-green-500/10 border-green-500/20 text-green-600 dark:text-green-400',
    labelKey: 'statusShipped'
  },
  archived: {
    icon: Archive,
    className: 'bg-muted/40 border-border/40 text-muted-foreground',
    labelKey: 'statusArchived'
  },
  contributed: {
    icon: Users,
    className: 'bg-blue-500/10 border-blue-500/20 text-blue-600 dark:text-blue-400',
    labelKey: 'statusContributed'
  }
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08 } }
};

const itemVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: 'easeOut' } }
};

export default function ProjectPage() {
  const { language, t } = useLanguage();
  const projects = useMemo(() => getProjects(language), [language]);

  return (
    <AcademicLayout>
      <PageMeta title="Projects" description={getText('projectsSubtitle', 'en')} path="/project" />
      <motion.div className="space-y-12" variants={containerVariants} initial="hidden" animate="visible">
        <motion.section variants={itemVariants}>
          <h1 className="text-2xl font-light text-foreground mb-3">{t('projectsTitle')}</h1>
          <p className="text-sm text-muted-foreground/80 leading-relaxed max-w-2xl">
            {t('projectsSubtitle')}
          </p>
        </motion.section>

        <motion.section variants={itemVariants} className="space-y-6">
          {projects.map(project => {
            const status = statusConfig[project.meta.status];
            const StatusIcon = status.icon;
            return (
              <motion.article
                key={project.slug}
                className="p-6 rounded-xl border border-border/50 hover:border-border/80 transition-all duration-300 bg-card/30 hover:bg-card/50"
                whileHover={{ y: -1 }}
                transition={{ type: 'spring', stiffness: 400 }}
              >
                <div className="space-y-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h2 className="text-lg font-medium text-foreground">{project.meta.title}</h2>
                      <p className="text-sm text-muted-foreground mt-1">
                        {project.meta.period}
                        {project.meta.role ? ` · ${project.meta.role}` : ''}
                      </p>
                    </div>
                    <span
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-md border font-medium shrink-0 ${status.className}`}
                    >
                      <StatusIcon className="w-3 h-3" />
                      {t(status.labelKey)}
                    </span>
                  </div>

                  <div className="text-sm text-muted-foreground/90 leading-relaxed [&_.prose]:text-sm">
                    <Markdown>{project.body}</Markdown>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    {project.meta.stack.map(item => (
                      <span
                        key={item}
                        className="px-2.5 py-1 text-xs bg-muted/30 text-foreground rounded-md border border-border/30"
                      >
                        {item}
                      </span>
                    ))}
                  </div>

                  {(project.meta.links?.github || project.meta.links?.external) && (
                    <div className="flex flex-wrap gap-4 pt-1">
                      {project.meta.links.github && (
                        <ExternalLink
                          href={project.meta.links.github}
                          className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
                        >
                          GitHub
                          <ArrowUpRight className="w-3.5 h-3.5" />
                        </ExternalLink>
                      )}
                      {project.meta.links.external && (
                        <ExternalLink
                          href={project.meta.links.external}
                          className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
                        >
                          {t('externalLink')}
                          <ArrowUpRight className="w-3.5 h-3.5" />
                        </ExternalLink>
                      )}
                    </div>
                  )}
                </div>
              </motion.article>
            );
          })}
        </motion.section>
      </motion.div>
    </AcademicLayout>
  );
}
